import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { AuditDetailResponse, Triage } from '../types';
import { updateFindingTriage } from '../api';

interface TriageOption {
  value: Triage;
  label: string;
  kind: 'commit' | 'note' | 'discuss' | 'closed';
}

// Untriaged is the default state — not shown as a clickable pill (you clear by re-clicking
// whichever pill is currently active).
const OPTIONS: TriageOption[] = [
  { value: 'will_address', label: 'Will address', kind: 'commit' },
  { value: 'acknowledged', label: 'Acknowledged', kind: 'note' },
  { value: 'discuss', label: 'Discuss', kind: 'discuss' },
  { value: 'by_design', label: 'By design', kind: 'closed' },
  { value: 'wont_fix', label: "Won't fix", kind: 'closed' }
];

interface FindingTriageSectionProps {
  auditId: string;
  findingId: string;
  triage: Triage;
  auditorNote: string;
}

export const FindingTriageSection = ({
  auditId,
  findingId,
  triage,
  auditorNote
}: FindingTriageSectionProps) => {
  // Local state for the note so the user can type without each keystroke firing a request.
  // We commit to the server on blur OR when the user explicitly clicks a triage pill (any
  // pending edits travel along with the triage update so we never lose a note).
  const [noteDraft, setNoteDraft] = useState(auditorNote);
  useEffect(() => setNoteDraft(auditorNote), [auditorNote]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: updateFindingTriage,
    // Optimistic update: apply the change to the cached audit immediately so the UI feels
    // snappy, then revert if the request fails.
    onMutate: async vars => {
      await queryClient.cancelQueries({ queryKey: ['audit', auditId] });
      const previous = queryClient.getQueryData<AuditDetailResponse>(['audit', auditId]);
      if (previous) {
        queryClient.setQueryData<AuditDetailResponse>(['audit', auditId], {
          ...previous,
          findings: previous.findings.map(f =>
            f.id === findingId ? { ...f, triage: vars.triage, auditorNote: vars.auditorNote } : f
          )
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['audit', auditId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['audit', auditId] });
    }
  });

  const commit = (nextTriage: Triage, nextNote: string) => {
    mutation.mutate({ auditId, findingId, triage: nextTriage, auditorNote: nextNote });
  };

  const onPillClick = (option: TriageOption) => {
    // Clicking the active pill clears the decision back to untriaged.
    const nextTriage: Triage = option.value === triage ? 'untriaged' : option.value;
    commit(nextTriage, noteDraft);
  };

  const onNoteBlur = () => {
    if (noteDraft === auditorNote) return; // no change → no request
    commit(triage, noteDraft);
  };

  return (
    <section className='triage' onClick={e => e.stopPropagation()}>
      <div className='triage__pills' role='group' aria-label='Triage decision'>
        {OPTIONS.map(opt => {
          const active = opt.value === triage;
          return (
            <button
              key={opt.value}
              type='button'
              aria-pressed={active}
              className={classNames(
                'triage__pill',
                `triage__pill--${opt.kind}`,
                active && 'triage__pill--active'
              )}
              onClick={() => onPillClick(opt)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <textarea
        className='triage__note'
        placeholder='Add a note…'
        rows={2}
        value={noteDraft}
        onChange={e => setNoteDraft(e.target.value)}
        onBlur={onNoteBlur}
      />
    </section>
  );
};

// Tiny presentational helper for the collapsed-card header. Just renders the current triage
// pill (if any) so collapse state still communicates the decision.
export const CollapsedTriagePill = ({ triage }: { triage: Triage }) => {
  if (triage === 'untriaged') return null;
  const option = OPTIONS.find(o => o.value === triage);
  if (!option) return null;
  return (
    <span className={classNames('triage-pill-mini', `triage-pill-mini--${option.kind}`)}>
      {option.label}
    </span>
  );
};
